import { SEOHead } from '../components/SEOHead';
import { COMPANY_CONFIG } from '../config/site';

export function TermsPage() {
  const lastUpdated = 'October 21, 2026';

  return (
    <>
      <SEOHead
        title="Terms of Use | Safaza Technologies, Inc."
        description="Terms of Use governing access to the website and digital materials of Safaza Technologies, Inc."
      />

      <section className="pt-16 pb-12 sm:pt-24 sm:pb-16 bg-white border-b border-[#E4E7EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D66E4]/8 border border-[#0D66E4]/20 text-[11px] font-mono font-medium text-[#0D66E4] mb-4">
            <span>LEGAL DOCUMENTATION</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#101828] mb-4">
            Terms of Use
          </h1>
          <p className="text-sm text-[#667085]">
            Last Updated: {lastUpdated} &middot; Effective Date: October 2026
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12 text-[#475467] leading-relaxed text-sm sm:text-base">
          
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using the website of {COMPANY_CONFIG.legalName} (&quot;Safaza Technologies&quot;,
              &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) at{' '}
              <span className="font-mono text-xs">{COMPANY_CONFIG.primaryDomain}</span>, you agree to
              be bound by these Terms of Use. If you do not agree to these terms, you should not access
              or use this website.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              2. Nature of Business &amp; Professional Scope
            </h2>
            <p className="mb-4">
              {COMPANY_CONFIG.legalName} is a software engineering company providing custom software
              development services and operating SaaS technology platforms (such as the software powering
              Akoole). This website is maintained to present our engineering capabilities, product portfolio,
              and corporate identity to clients, commercial partners, and site visitors.
            </p>
            <div className="p-4 rounded-xl bg-[#F6F8FB] border border-[#E4E7EC] text-[#475467] text-xs sm:text-sm">
              <p className="font-semibold text-[#101828] mb-1">Scope of Operations Notice:</p>
              <p>
                Safaza Technologies, Inc. is a software development company. We do not provide banking,
                money transmission, payment processing, deposit-taking, currency exchange, or financial
                custodial services. Nothing on this website constitutes financial advice, money services,
                or an offer of regulated financial products.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              3. Intellectual Property Rights
            </h2>
            <p className="mb-4">
              All content on this website—including without limitation the Safaza brand marks,
              typography, layout design, software descriptions, text, diagrams, code samples, and
              graphics—is the exclusive property of {COMPANY_CONFIG.legalName} or its licensors and is
              protected by United States and international copyright, trademark, and trade dress laws.
            </p>
            <p>
              You may view and print pages from this website solely for your own internal,
              non-commercial evaluation purposes. You may not copy, reproduce, distribute, modify,
              or create derivative works of any material without our prior written consent.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              4. Commercial Agreements &amp; Product Roadmaps
            </h2>
            <p className="mb-4">
              Descriptions of custom software engineering services, SaaS platforms, and roadmaps
              represent technical capabilities and product specifications. Commercial software
              deliverables and client engagements are governed by separate, executed Master Services
              Agreements (MSA), Statements of Work (SOW), or End User License Agreements (EULA).
            </p>
            <p>
              We reserve the right to modify, enhance, or adjust software specifications and product
              features as technical requirements and commercial roadmaps evolve.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              5. Acceptable Use of Website
            </h2>
            <p className="mb-4">
              You agree to use this website only for lawful purposes. You shall not:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Transmit any malicious code, virus, worm, or automated script;</li>
              <li>Attempt to gain unauthorized access to any system, server, or database connected to this website;</li>
              <li>Submit fraudulent, abusive, or spam communications through the contact form;</li>
              <li>Scrape or harvest data from this site for unauthorized commercial distribution.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              6. Disclaimer of Warranties
            </h2>
            <p className="mb-4">
              This website and all information provided herein are provided on an &quot;AS IS&quot; and &quot;AS
              AVAILABLE&quot; basis, without warranties of any kind, either express or implied, including
              but not limited to implied warranties of merchantability, fitness for a particular purpose,
              or non-infringement.
            </p>
            <p>
              We do not warrant that this website will be uninterrupted, error-free, or free from viruses
              or other harmful components.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              7. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by applicable law, in no event shall {COMPANY_CONFIG.legalName},
              its directors, officers, employees, or affiliates be liable for any indirect, incidental,
              special, consequential, or punitive damages arising out of or related to your use of, or
              inability to use, this website.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              8. Governing Law &amp; Jurisdiction
            </h2>
            <p>
              These Terms of Use shall be governed by and construed in accordance with the laws of the
              United States, without giving effect to any principles of conflicts of law.
            </p>
          </div>

          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#101828] mb-4">
              9. Contact Information
            </h2>
            <p>
              For legal inquiries regarding these Terms of Use, please contact:
            </p>
            <div className="mt-4 p-5 rounded-xl bg-[#F6F8FB] border border-[#E4E7EC] text-xs sm:text-sm font-mono space-y-1">
              <p className="font-bold text-[#101828]">{COMPANY_CONFIG.legalName}</p>
              <p>Attn: Corporate Legal Inquiries</p>
              <p>Jurisdiction: {COMPANY_CONFIG.incorporationCountry}</p>
              <p>
                Email:{' '}
                <a href={`mailto:${COMPANY_CONFIG.contactEmail}`} className="text-[#0D66E4] hover:underline">
                  {COMPANY_CONFIG.contactEmail}
                </a>
              </p>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}
